import { Socket, SocketIoConfig } from 'ngx-socket-io';
import { ExternalLinkService } from './external-link.service';
import { ApplicationRef, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { AuthenticationService } from 'src/shared/auth';
import IActiveConnections = Authorization.IActiveConnections;
import { IBaseFieldComponent } from 'src/app/interfaces/base-field-component.interface';

enum ZCSyncEvents {
    REGISTER_ZC_EDITING = 'registerZCEditing',
    NEW_USER_CONNECTION = 'newUserConnection',
    ADD_OR_UPDATE_ACTION = 'addOrUpdateEntities',
    DELETE_ACTION = 'deleteEntities',
    SYNC_ACTION = 'syncFromBaseZC'
}

@Injectable()
export class SyncService {
    private socket: Socket;
    private changesSubject = new Subject<unknown>();
    private deleteChangesSubject = new Subject<unknown>();
    private syncChangesSubject = new Subject<unknown>();
    private connectedUserSubject = new Subject<IActiveConnections>();
    private changeDetectionSubject = new Subject<number>();
    private valueChangedSubject = new Subject<IBaseFieldComponent | undefined>();

    changes = this.changesSubject.asObservable().pipe(distinctUntilChanged());
    deleteChanges = this.deleteChangesSubject.asObservable().pipe(distinctUntilChanged());
    syncChanges = this.syncChangesSubject.asObservable().pipe(distinctUntilChanged());
    connectedUsers = this.connectedUserSubject.asObservable().pipe(distinctUntilChanged());
    changeDetection = this.changeDetectionSubject.asObservable().pipe();
    valueChanged = this.valueChangedSubject.asObservable().pipe();

    constructor(
        private readonly authenticationService: AuthenticationService,
        private readonly externalLinkService: ExternalLinkService,
        private readonly appRef: ApplicationRef
    ) {
        this.initSocket();
        this.initSocketEvents();
    }

    registerZCEditing() {
        const token = this.authenticationService.token,
            externalLink = this.externalLinkService.ExternalLink;
        if (!token || !externalLink) {
            return;
        }
        this.socket.emit(ZCSyncEvents.REGISTER_ZC_EDITING, { token, externalLink });
    }

    private initSocket() {
        const socketConfig = this.getSocketConfig();
        this.socket = new Socket(socketConfig as SocketIoConfig, this.appRef);
    }

    private initSocketEvents() {
        this.socket.on('connect', () => {
            this.registerZCEditing();
        });

        this.socket.on(ZCSyncEvents.ADD_OR_UPDATE_ACTION, data => {
            this.changesSubject.next(data);
        });

        this.socket.on(ZCSyncEvents.DELETE_ACTION, data => {
            this.deleteChangesSubject.next(data);
        });

        this.socket.on(ZCSyncEvents.SYNC_ACTION, data => {
            this.syncChangesSubject.next(data);
        });

        this.socket.on(ZCSyncEvents.NEW_USER_CONNECTION, (data: IActiveConnections) => {
            this.connectedUserSubject.next(data);
        });
    }

    private getSocketConfig() {
        const socketConfig: any = {
            options: {
                reconnectionAttempts: 20
            }
        };

        const calibrationUrl = environment.ZONEIQ_CALIBRATION;

        if (calibrationUrl.startsWith('http')) {
            socketConfig.url = new URL(calibrationUrl).origin;
        } else {
            socketConfig.options.hostname = window.location.hostname;
            socketConfig.options.path = '/' + calibrationUrl + '/socket.io';
        }
        return socketConfig;
    }

    triggerChangeDetection() {
        this.changeDetectionSubject.next(Date.now());
        this.valueChangedSubject.next(undefined);
    }

    emitValueChanged(value: IBaseFieldComponent) {
        this.valueChangedSubject.next(value);
    }
}
